import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import Segment from "./Segment";
import { useJeMobil, omezenyPohyb } from "../lib/mobil";
import { Kbd } from "../lib/zkratky";

/**
 * Zobrazení obchodních případů (V): jak se seznam ukazuje — rozvržení, seskupení,
 * řazení, vlastnosti na kartě a kolik uzavřených. „Filtr říká, co vidím,
 * zobrazení říká jak.“ Ukládá se pro uživatele v prohlížeči; bez úložiště jde vše dál.
 */
export const VLASTNOSTI = [
  ["hodnota", "Hodnota"], ["klient", "Klient"], ["technologie", "Technologie"],
  ["obchodnik", "Obchodník"], ["krok", "Další krok"], ["dni", "Dní ve fázi"],
];
const vychozi = (mobil) => ({
  rozvrzeni: "seznam", seskupit: "faze", razeni: "zmena", smer: "desc", predano: "30", ztracene: false,
  vlastnosti: { hodnota: true, klient: true, technologie: !mobil, obchodnik: true, krok: true, dni: true },
});

export function useZobrazeni(userId) {
  const mobil = useJeMobil();
  const klic = `pp-obchod-zobrazeni:${userId || "anon"}`;
  const [z, setZ] = useState(() => {
    try {
      const ulozene = JSON.parse(window.localStorage.getItem(klic) || "null");
      if (ulozene) return { ...vychozi(mobil), ...ulozene, vlastnosti: { ...vychozi(mobil).vlastnosti, ...(ulozene.vlastnosti || {}) } };
    } catch { /* bez úložiště výchozí */ }
    return vychozi(mobil);
  });
  const nastav = (zmena) => setZ((s) => {
    const nove = { ...s, ...(typeof zmena === "function" ? zmena(s) : zmena) };
    try { window.localStorage.setItem(klic, JSON.stringify(nove)); } catch { /* jen v paměti */ }
    return nove;
  });
  const reset = () => {
    try { window.localStorage.removeItem(klic); } catch { /* nic */ }
    setZ(vychozi(mobil));
  };
  return [z, nastav, reset];
}

/** Od kdy brát předané a ztracené případy (YYYY-MM-DD), nebo null = vše. */
export function uzavreneOd(z) {
  if (z.predano === "vse") return null;
  const d = new Date();
  d.setDate(d.getDate() - Number(z.predano || 30));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Volba({ moznosti, hodnota, onZmena, testid }) {
  return (
    <Segment className="inline-flex flex-wrap gap-0.5 p-[3px] rounded-[10px] bg-inputbg max-w-full" jezdec="bg-white shadow-card" data-testid={testid}>
      {moznosti.map(([v, label]) => (
        <button key={v} type="button" data-on={hodnota === v} onClick={() => onZmena(v)} data-testid={`${testid}-${v}`}
          className={`tap md:min-h-0 relative h-10 md:h-7 px-2.5 rounded-[8px] t-meta font-700 ${hodnota === v ? "text-ink" : "text-sec hover:text-ink"}`}>
          {label}
        </button>
      ))}
    </Segment>
  );
}

export function ZobrazeniPanel({ z, nastav, reset, money, kotva, onClose }) {
  const mobil = useJeMobil();
  const ref = useRef(null);
  const [poloha, setPoloha] = useState(null);
  useLayoutEffect(() => {
    if (mobil || !kotva) return;
    const r = kotva.getBoundingClientRect();
    setPoloha({ right: Math.max(8, window.innerWidth - r.right), top: r.bottom + 6 });
  }, [mobil, kotva]);
  useEffect(() => {
    const klik = (e) => { if (!ref.current?.contains(e.target) && !kotva?.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose(); kotva?.focus(); } };
    document.addEventListener("mousedown", klik);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", klik); document.removeEventListener("keydown", esc); };
  }, [kotva, onClose]);

  const radek = (popis, obsah) => (
    <div className="grid grid-cols-1 md:grid-cols-[76px_1fr] items-center gap-1.5 md:gap-3">
      <span className="t-meta font-600 text-sec">{popis}</span><div className="min-w-0">{obsah}</div>
    </div>
  );
  const razeni = [["zmena", "Poslední změna"], ...(money ? [["hodnota", "Hodnota"]] : []), ["krok", "Další krok"],
    ["dni", "Dní ve fázi"], ["nazev", "Název"]];
  const telo = (
    <div className="p-3.5 grid gap-3" data-testid="zobrazeni-panel">
      {radek("Rozvržení", <Volba testid="zobrazeni-rozvrzeni" hodnota={z.rozvrzeni} onZmena={(v) => nastav({ rozvrzeni: v })}
        moznosti={[["seznam", "Seznam"], ["tabule", "Tabule"]]} />)}
      {z.rozvrzeni === "seznam" && radek("Seskupit", <Volba testid="zobrazeni-seskupit" hodnota={z.seskupit} onZmena={(v) => nastav({ seskupit: v })}
        moznosti={[["faze", "Fáze"], ["obchodnik", "Obchodník"], ["technologie", "Technologie"], ["nic", "Nic"]]} />)}
      {radek("Řadit", (
        <span className="flex items-center gap-1.5">
          <Volba testid="zobrazeni-razeni" hodnota={z.razeni} onZmena={(v) => nastav({ razeni: v })} moznosti={razeni} />
          <button type="button" onClick={() => nastav((s) => ({ smer: s.smer === "asc" ? "desc" : "asc" }))} data-testid="zobrazeni-smer"
            aria-label={`Směr řazení: ${z.smer === "asc" ? "vzestupně" : "sestupně"}`}
            className="tap md:min-h-0 md:min-w-0 h-10 w-10 md:h-7 md:w-7 shrink-0 rounded-[8px] border border-line-strong bg-white inline-flex items-center justify-center text-sec hover:text-ink">
            {z.smer === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
          </button>
        </span>
      ))}
      {radek("Vlastnosti", (
        <span className="flex flex-wrap gap-1.5">
          {VLASTNOSTI.filter(([k]) => money || k !== "hodnota").map(([k, label]) => (
            <button key={k} type="button" aria-pressed={!!z.vlastnosti[k]} data-testid={`zobrazeni-vlastnost-${k}`}
              onClick={() => nastav((s) => ({ vlastnosti: { ...s.vlastnosti, [k]: !s.vlastnosti[k] } }))}
              className={`tap md:min-h-0 h-10 md:h-7 px-2.5 rounded-[8px] border t-meta font-600 ${
                z.vlastnosti[k] ? "bg-ink border-ink text-white" : "bg-white border-line-strong text-sec hover:text-ink"}`}>{label}</button>
          ))}
        </span>
      ))}
      {radek("Předáno", <Volba testid="zobrazeni-predano" hodnota={z.predano} onZmena={(v) => nastav({ predano: v })}
        moznosti={[["30", "30 dní"], ["90", "90 dní"], ["vse", "Vše"]]} />)}
      {radek("Ztracené", <Volba testid="zobrazeni-ztracene" hodnota={z.ztracene ? "ano" : "ne"} onZmena={(v) => nastav({ ztracene: v === "ano" })}
        moznosti={[["ne", "Skrýt"], ["ano", "Ukázat"]]} />)}
      <div className="flex items-center gap-3 pt-2 border-t border-line-softer">
        <span className="t-meta text-muted flex-1">Filtr říká, co vidím. Zobrazení říká jak.</span>
        <button type="button" onClick={reset} className="tap md:min-h-0 t-meta font-700 text-sec hover:text-ink" data-testid="zobrazeni-vychozi">Výchozí</button>
      </div>
    </div>
  );

  if (mobil) {
    return createPortal(
      <div className="fixed inset-0 z-[80] flex items-end">
        <div className="absolute inset-0 bg-black/30 zaclon-in" onClick={onClose} />
        <div ref={ref} role="dialog" aria-modal="true" aria-label="Zobrazení"
          className="karta-panel relative bg-white w-full rounded-t-sheet shadow-panel max-h-[85vh] overflow-y-auto"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="flex items-center px-4 pt-3">
            <span className="t-sekce font-700 text-ink flex-1">Zobrazení</span>
            <button type="button" onClick={onClose} aria-label="Zavřít" className="tap -mr-2 text-muted"><X size={20} /></button>
          </div>
          {telo}
        </div>
      </div>, document.body);
  }
  if (!poloha) return null;
  return createPortal(
    <div ref={ref} role="dialog" aria-label="Zobrazení"
      className={`${omezenyPohyb() ? "" : "vyber-menu"} fixed z-[200] w-[392px] bg-white rounded-[14px] border border-line-softer shadow-panel`}
      style={{ right: poloha.right, top: poloha.top }}>
      {telo}
    </div>, document.body);
}

const ZKRATKY = [["Nový případ", "C"], ["Hledat", "/"], ["Filtr", "F"], ["Zobrazení", "V"], ["Seznam / Tabule", "1 · 2"],
  ["Zavřít menu nebo panel", "Esc"], ["Tahák zkratek", "?"]];

/** Tahák zkratek (?). */
export function TahakZkratek({ onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="tahak-zkratek">
      <div className="absolute inset-0 bg-black/20 zaclon-in" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="Klávesové zkratky"
        className="okno-in relative bg-white w-[300px] rounded-[14px] shadow-panel p-3.5">
        <div className="flex items-center mb-1.5">
          <span className="t-sekce font-700 text-ink flex-1">Klávesové zkratky</span>
          <button type="button" onClick={onClose} aria-label="Zavřít" className="text-muted hover:text-ink"><X size={16} /></button>
        </div>
        {ZKRATKY.map(([popis, klavesa]) => (
          <div key={popis} className="min-h-[30px] flex items-center justify-between t-meta text-sec">
            <span>{popis}</span><Kbd>{klavesa}</Kbd>
          </div>
        ))}
      </div>
    </div>, document.body);
}
